import {
  Component,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ViewChildren,
  QueryList,
  ElementRef,
  OnDestroy,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  Chart as ChartJS,
  ChartConfiguration,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend,
  Filler
);

export interface InvestmentItem {
  companyName: string;
  amount: string;
  percentageChange: string;
  isPositive: boolean;
  chartData: number[];
}

import { ModalService } from '../../../../shared/services/modal.service';
import { OverlayModule } from '@angular/cdk/overlay';
import { Subscription } from 'rxjs';
import { FiDataService, FIDataResponse } from '../../../../core/services/fi-data.service';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';

@Component({
  selector: 'app-investment-overview-widget',
  standalone: true,
  imports: [CommonModule, WidgetEdit, OverlayModule],
  templateUrl: './investment-overview-widget.html',
  styleUrl: './investment-overview-widget.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class InvestmentOverviewWidgetComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() investments: InvestmentItem[] = [];
  @Input() isEditMode = false;
  @Input() isAddMode = false;
  @Output() addWidget = new EventEmitter<void>();
  @Output() editCall = new EventEmitter<any>();

  private subscription: Subscription = new Subscription();
  isLoading = true;
  currentPage = 0;
  private rafId: number = 0;

  @ViewChildren('trendChart') trendCharts!: QueryList<ElementRef<HTMLCanvasElement>>;
  private charts: ChartJS[] = [];

  @Input() size: 'small' | 'medium' | 'half' | 'full' = 'small';
  @Input() index = 0;
  @Input() activeDropdownIndex: number | null = null;
  @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
  @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
  @Input() changeWidgetSize!: (index: number, size: any) => void;
  @Input() removeWidget!: (index: number) => void;
  @Input() title = 'Investment Overview';

  constructor(
    private modalService: ModalService,
    private fiDataService: FiDataService,
    private cdr: ChangeDetectorRef
  ) { }

  openModal(assetName: string) {
    if (this.isEditMode || this.isAddMode) return;
    this.modalService.openInvestmentOverviewModal(assetName);
    this.cdr.detectChanges();
  }

  ngOnInit() {
    this.subscription.add(
      this.fiDataService.fiData$.subscribe((response: FIDataResponse) => {
        if (response && response.status === 'success' && Array.isArray(response.data)) {
          this.processInvestments(response.data);
          this.isLoading = false;
          this.cdr.markForCheck();

          // Wait for Angular to update the DOM before initializing new charts
          cancelAnimationFrame(this.rafId);
          this.rafId = requestAnimationFrame(() => {
            this.destroyCharts();
            this.initializeCharts();
            this.cdr.markForCheck();
          });
        }
      })
    );
  }

  private processInvestments(data: any[]): void {
    const investmentTypes = ['EQUITIES', 'MUTUAL_FUNDS', 'ETF'];

    this.investments = data
      .filter(item => investmentTypes.includes(item.fiType))
      .map((item, index) => {
        const summary = item.Summary || {};
        let name = summary.companyName || summary.issuerName || summary.schemeName || item.bank || item.fiType;
        const balance = parseFloat(summary.currentValue || summary.balance || 0);

        if (item.fiType === 'MUTUAL_FUNDS') name = `${name} (MF)`;
        else if (item.fiType === 'ETF') name = `${name} (ETF)`;

        return {
          companyName: name,
          amount: `₹${balance.toLocaleString('en-IN')}`,
          percentageChange: '+0.0%',
          isPositive: true,
          chartData: this.generateSimulatedTrend(index)
        };
      });
  }

  private generateSimulatedTrend(index: number): number[] {
    const trends = [
      [30, 45, 35, 55, 40, 70, 50, 85, 65, 90],
      [80, 70, 75, 60, 65, 50, 55, 40, 45, 35],
      [40, 45, 42, 48, 45, 52, 50, 55, 53, 60],
      [60, 50, 65, 55, 70, 60, 75, 65, 80, 70]
    ];
    return trends[index % trends.length];
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['size'] && !changes['size'].firstChange) {
      this.currentPage = 0;
      this.destroyCharts();
      // Re-init charts in sync with browser paint
      cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => this.reinitializeCharts());
    }
  }

  ngAfterViewInit() {
    // Initial charts draw synced to paint
    this.rafId = requestAnimationFrame(() => this.initializeCharts());
  }

  private initializeCharts() {
    this.trendCharts.forEach((chartRef, index) => {
      const canvas = chartRef.nativeElement;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const existingChart = ChartJS.getChart(canvas);
      if (existingChart) existingChart.destroy();

      const investment = this.getVisibleInvestments()[index];
      if (!investment) return;

      const borderColor = investment.isPositive ? '#26BC8F' : '#EF4D6C';
      const strokeStyleColor = investment.isPositive
        ? 'rgba(38, 188, 143, 0.8)'
        : 'rgba(239, 77, 108, 0.8)';

      const middleLinePlugin = {
        id: 'middleLine',
        afterDatasetsDraw: (chart: any) => {
          const ctx = chart.ctx;
          const chartArea = chart.chartArea;
          const yAxis = chart.scales.y;

          const dataValues = chart.data.datasets[0].data;
          const minValue = Math.min(...dataValues);
          const maxValue = Math.max(...dataValues);
          const middleValue = (minValue + maxValue) / 2;
          const yPos = yAxis.getPixelForValue(middleValue);

          ctx.save();
          ctx.strokeStyle = strokeStyleColor;
          ctx.lineWidth = 1;
          ctx.setLineDash([3, 3]);
          ctx.beginPath();
          ctx.moveTo(chartArea.left, yPos);
          ctx.lineTo(chartArea.right, yPos);
          ctx.stroke();
          ctx.restore();
        }
      };

      const chartConfig: ChartConfiguration = {
        type: 'line',
        data: {
          labels: investment.chartData.map((_, i) => `${i}`),
          datasets: [{
            data: investment.chartData,
            borderColor,
            backgroundColor: 'transparent',
            borderWidth: 1.5,
            tension: 0.4,
            pointRadius: 0,
            pointHoverRadius: 0,
            fill: false
          }]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false }
          },
          scales: {
            x: { display: false, grid: { display: false } },
            y: { display: false, grid: { display: false } }
          }
        },
        plugins: [middleLinePlugin]
      };

      const chart = new ChartJS(ctx, chartConfig);
      this.charts.push(chart);
    });
  }

  emitEditEvent(eve: any): void {
    this.editCall.emit(eve);
  }

  /* ------------------------ FIXED GRID LOGIC ------------------------ */

  getColumnCount(): number {
    if (this.size === 'small') return 1;
    if (this.size === 'medium') return 2;
    if (this.size === 'half') return 3;
    return 6; // full
  }

  getItemsPerPage(): number {
    return this.getColumnCount() * 2;
  }

  getColumnsArray(): number[] {
    return Array(this.getColumnCount()).fill(0);
  }

  getColumnInvestments(columnIndex: number): InvestmentItem[] {
    const itemsPerPage = this.getItemsPerPage();
    const start = this.currentPage * itemsPerPage + columnIndex * 2;
    return this.investments.slice(start, start + 2);
  }

  getVisibleInvestments(): InvestmentItem[] {
    const start = this.currentPage * this.getItemsPerPage();
    return this.investments.slice(start, start + this.getItemsPerPage());
  }

  /* ------------------------ PAGINATION ------------------------ */

  nextPage(event: Event) {
    event.stopPropagation();
    if (this.canGoNext()) {
      this.currentPage++;
      this.destroyCharts();
      this.cdr.markForCheck();
      cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => {
        this.initializeCharts();
        this.cdr.markForCheck();
      });
    }
  }

  prevPage(event: Event) {
    event.stopPropagation();
    if (this.canGoPrev()) {
      this.currentPage--;
      this.destroyCharts();
      this.cdr.markForCheck();
      cancelAnimationFrame(this.rafId);
      this.rafId = requestAnimationFrame(() => {
        this.initializeCharts();
        this.cdr.markForCheck();
      });
    }
  }

  canGoNext(): boolean {
    return (this.currentPage + 1) * this.getItemsPerPage() < this.investments.length;
  }

  canGoPrev(): boolean {
    return this.currentPage > 0;
  }

  /* ------------------------ CHART CLEANUP ------------------------ */

  private destroyCharts() {
    this.charts.forEach(chart => chart.destroy());
    this.charts = [];
  }

  private reinitializeCharts() {
    if (this.trendCharts && this.trendCharts.length > 0) {
      this.initializeCharts();
    }
  }

  ngOnDestroy() {
    cancelAnimationFrame(this.rafId);
    this.subscription.unsubscribe();
    this.destroyCharts();
  }
}
